import { marked } from 'marked';

/**
 * Checks if a table block contains quantitative data (numbers).
 * @param {string[]} lines - The lines of the table block
 * @returns {boolean} - True if the table has at least one numeric column
 */
const isQuantitativeTable = (lines) => {
  // Find the separator row (usually |---|---|)
  const separatorIdx = lines.findIndex(line => line.trim().includes('---'));
  if (separatorIdx === -1) return false;

  const dataRows = lines.slice(separatorIdx + 1);
  if (dataRows.length === 0) return false;

  // Analyze columns
  const columns = [];

  dataRows.forEach(row => {
    const cells = row.split('|');
    const trimmedRow = row.trim();
    // Remove first and last empty strings if the row starts/ends with pipe
    if (trimmedRow.startsWith('|')) cells.shift();
    if (trimmedRow.endsWith('|')) cells.pop();

    cells.forEach((cell, idx) => {
      if (!columns[idx]) columns[idx] = [];
      columns[idx].push(cell.trim());
    });
  });

  // Check if any column is predominantly numeric
  return columns.some(col => {
    if (col.length === 0) return false;
    const numericCount = col.filter(cell => {
      // Remove currency symbols, commas, percentages, and whitespace
      const clean = cell.replace(/[$,%\s]/g, '');
      return clean !== '' && !isNaN(Number(clean));
    }).length;
    return numericCount / col.length > 0.5;
  });
};

/**
 * Process chat markdown output and return structured blocks for creating data views.
 * Splits content by top-level headers (#) and identifies markdown tables.
 * @param {string} markdown - The raw markdown content from the chat
 * @returns {Array<{type: 'text'|'table'|'diagram', content: string}>} - Array of content blocks
 */
export const processChatOutput = (markdown) => {
  const chartTypes = ["graph TD", "graph LR", "sequenceDiagram", "gantt"];
  const blocks = [];
  const lines = markdown.split('\n');
  let currentBlock = { type: 'text', lines: [] };
  let inMermaidBlock = false;

  const pushBlock = () => {
    if (currentBlock.lines.length > 0 && currentBlock.lines.some(l => l.trim())) {
      const wasTable = currentBlock.type === 'table';
      if (wasTable && !isQuantitativeTable(currentBlock.lines)) {
        currentBlock.type = 'text';
      }

      if (currentBlock.type === 'text') {
        const firstLine = currentBlock.lines.find(l => l.trim() !== '');
        if (firstLine) {
            const trimmedFirstLine = firstLine.trim();
            if (chartTypes.some(chartType => trimmedFirstLine.startsWith(chartType))) {
                currentBlock.type = 'diagram';
            }
        }
      }

      if (currentBlock.type === 'table') {
        blocks.push({ type: 'table', content: currentBlock.lines.join('\n') });
      } else if (currentBlock.type === 'diagram') {
        blocks.push({ type: 'diagram', content: currentBlock.lines.join('\n') });
      } else {
        // Use marked to convert markdown to HTML
        blocks.push({ type: 'text', content: marked(currentBlock.lines.join('\n')) });
      }
    }
  };

  const hrRegex = /^(<hr>|(?:- *){3,}|(?:_ *){3,}|(?:\* *){3,})$/;
  const mermaidStartRegex = /^```mermaid/;
  const codeFenceRegex = /^```/;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    if (inMermaidBlock) {
      if (codeFenceRegex.test(trimmedLine)) {
        inMermaidBlock = false;
        pushBlock();
        currentBlock = { type: 'text', lines: [] };
      } else {
        currentBlock.lines.push(line);
      }
      continue;
    }

    if (mermaidStartRegex.test(trimmedLine)) {
      pushBlock(); // push whatever was before
      currentBlock = { type: 'diagram', lines: [] };
      inMermaidBlock = true;
      continue;
    }

    // Check for Horizontal Rule
    if (hrRegex.test(trimmedLine)) {
      pushBlock();
      // A horizontal rule signifies a new block, but we don't include the rule itself.
      currentBlock = { type: 'text', lines: [] };
      continue;
    }

    // Check for Table Row
    const isTableLine = trimmedLine.startsWith('|');

    if (isTableLine) {
      if (currentBlock.type !== 'table') {
        // Switching from Text to Table
        // Check if the preceding text block looks like a header for this table
        // Heuristic: It's a header if it's short (e.g., <= 3 lines) or lines start with #
        const isHeaderBlock = currentBlock.lines.length > 0 && (
          currentBlock.lines.length <= 3 || 
          currentBlock.lines.every(l => l.trim().startsWith('#'))
        );

        if (isHeaderBlock) {
          // Convert current text block to table block to keep them together
          currentBlock.type = 'table';
          currentBlock.lines.push(line);
        } else {
          pushBlock();
          currentBlock = { type: 'table', lines: [line] };
        }
      } else {
        // Continue Table
        currentBlock.lines.push(line);
      }
    } else {
      if (currentBlock.type === 'table') {
        // Switching from Table back to Text
        pushBlock();
        currentBlock = { type: 'text', lines: [line] };
      } else {
        // Continue Text
        currentBlock.lines.push(line);
      }
    }
  }

  // Push the final block
  pushBlock();

  // Do not merge blocks to allow for separate slides/blocks.
  return blocks;
};